import React, { useEffect, useState } from 'react';
import {
  Eyebrow,
  FeatureList,
  HeroCopy,
  HeroInner,
  HeroSection,
  HeroShopLinks,
  HeroShopRail,
  HeroSocialLink,
  PathwayGrid,
  PathwayPanel,
  PageContainer,
  Section,
  SectionHeading,
  SectionInner,
  SocialLinksContainer,
  SocialLink,
  LinkIcon,
  StoryCard,
  StoryGrid,
  Subtitle,
  Title,
  TitleLockup,
  TitleShell
} from './App.styles';

import { IconBaseProps } from 'react-icons';
import { SiInstagram, SiTiktok } from 'react-icons/si';
import './App.css';

type SocialPlatform = {
  name: string;
  href: string;
  icon: React.ComponentType<IconBaseProps>;
};

const PlaceholderDot: React.ComponentType<IconBaseProps> = props => (
  <svg
    viewBox="0 0 16 16"
    fill="currentColor"
    aria-hidden
    focusable="false"
    width="1em"
    height="1em"
    {...props}
  >
    <circle cx="8" cy="8" r="8" />
  </svg>
);

const socialPlatforms: SocialPlatform[] = [
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/aphroditeattire',
    icon: SiInstagram as React.ComponentType<IconBaseProps>
  },
  {
    name: 'TikTok',
    href: 'https://www.tiktok.com/@aphroditeattire',
    icon: SiTiktok as React.ComponentType<IconBaseProps>
  },
  {
    name: 'Depop',
    href: 'https://www.depop.com/aphroditeattire/',
    icon: PlaceholderDot
  },
  {
    name: 'Poshmark',
    href: 'https://poshmark.com/closet/aphroditeattire',
    icon: PlaceholderDot
  },
  {
    name: 'Vinted',
    href: 'https://www.vinted.com/member/229740558',
    icon: PlaceholderDot
  }
];

function App() {
  const isAdminPath = window.location.pathname.startsWith('/admin');
  const adminEnabled = process.env.REACT_APP_ENABLE_ADMIN === 'true';
  const [AdminApp, setAdminApp] = useState<React.ComponentType | null>(null);

  useEffect(() => {
    if (!isAdminPath) {
      return;
    }

    if (!adminEnabled) {
      window.location.replace('/');
      return;
    }

    void import('./admin/AdminApp').then(module => {
      setAdminApp(() => module.AdminApp);
    });
  }, [adminEnabled, isAdminPath]);

  if (isAdminPath) {
    if (!adminEnabled) {
      return null;
    }

    if (!AdminApp) {
      return (
        <main className="admin-loading-shell">
          <aside className="admin-loading-sidebar">
            <strong>Aphrodite</strong>
            <span>RESALE WORKSPACE</span>
          </aside>
          <section className="admin-loading-main" aria-live="polite">
            <div className="admin-loading-card">
              <span className="admin-loading-kicker">Admin</span>
              <h1>Loading admin interface...</h1>
              <div className="admin-loading-line" />
              <div className="admin-loading-line short" />
            </div>
          </section>
        </main>
      );
    }

    return <AdminApp />;
  }

  return (
    <PageContainer>
      <HeroSection>
        <HeroInner>
          <HeroCopy>
            <TitleLockup>
              <TitleShell src="/brand/logos/seashell/AA-Seashell-Coral_outline.png" alt="" />
              <Title>Aphrodite Attire</Title>
            </TitleLockup>
            <Subtitle>Slip into something unforgettable.</Subtitle>
            <HeroShopRail aria-label="Shop channels">
              <HeroShopLinks>
                {socialPlatforms.map(({ name, href, icon: Icon }) => (
                  <HeroSocialLink key={`hero-${name}`} href={href} target="_blank" rel="noopener noreferrer">
                    <LinkIcon aria-hidden>
                      <Icon />
                    </LinkIcon>
                    <span>{name}</span>
                  </HeroSocialLink>
                ))}
              </HeroShopLinks>
            </HeroShopRail>
          </HeroCopy>
        </HeroInner>
      </HeroSection>

      <Section $tone="blush" aria-labelledby="brand-point-heading">
        <SectionInner>
          <SectionHeading>
            <Eyebrow>Brand point of view</Eyebrow>
            <h2 id="brand-point-heading">Curated for the girls who dress like the moment matters</h2>
            <p>
              Aphrodite Attire is a curated resale collection built around feminine textures, statement silhouettes,
              glossy details, and pieces that make getting dressed feel exciting again. Think soft seduction, vacation
              energy, and outfits that deserve to be seen.
            </p>
          </SectionHeading>
          <StoryGrid>
            <StoryCard>
              <h3>Flattering first</h3>
              <p>We gravitate toward pieces with movement, shape, texture, and a little magnetism. The kind of finds people ask about later.</p>
            </StoryCard>
            <StoryCard>
              <h3>Collected with intention</h3>
              <p>Every piece is chosen for its mood, detail, and ability to stand out — not just because it&apos;s trending for five minutes.</p>
            </StoryCard>
            <StoryCard>
              <h3>Curated, not cluttered</h3>
              <p>A cleaner, more intentional resale experience with pieces that feel elevated, wearable, and worth the screenshot.</p>
            </StoryCard>
          </StoryGrid>
        </SectionInner>
      </Section>

      <Section id="shop-pathways" $tone="sky" aria-labelledby="shop-pathways-heading">
        <SectionInner>
          <SectionHeading>
            <Eyebrow>Shop pathways</Eyebrow>
            <h2 id="shop-pathways-heading">Shop Aphrodite your way</h2>
            <p>
              From Depop drops to TikTok styling videos, Aphrodite Attire moves across platforms while keeping the same
              feminine, glossy point of view.
            </p>
          </SectionHeading>
          <PathwayGrid>
            <PathwayPanel>
              <h3>Shop the storefronts</h3>
              <p>
                Browse marketplace drops, save favorites, and keep up with the latest listings across Aphrodite
                Attire&apos;s active channels.
              </p>
              <SocialLinksContainer>
                {socialPlatforms.map(({ name, href, icon: Icon }) => (
                  <SocialLink key={name} href={href} target="_blank" rel="noopener noreferrer">
                    <LinkIcon aria-hidden>
                      <Icon />
                    </LinkIcon>
                    <span>{name}</span>
                  </SocialLink>
                ))}
              </SocialLinksContainer>
            </PathwayPanel>
            <PathwayPanel>
              <h3>Expect a softer landing</h3>
              <p>
                Aphrodite Attire is meant to feel more like stepping into a moodboard than scrolling a crowded
                marketplace. Soft textures, statement pieces, glossy details, and finds that feel a little
                unforgettable.
              </p>
              <FeatureList>
                <li>Feminine textures, soft tailoring, and pieces with enough character to style several ways.</li>
                <li>Clearer presentation and more grounded listing details as the workflow keeps improving behind the scenes.</li>
                <li>New finds surfacing across Depop, Poshmark, Vinted, and social channels throughout each haul.</li>
              </FeatureList>
            </PathwayPanel>
          </PathwayGrid>
        </SectionInner>
      </Section>
    </PageContainer>
  );
}

export default App;
